const PREFIX = '[Termly]'

/**
 * Prints a structured diagnostic describing whether Google tags fired
 * before Termly set its GCM consent defaults. Intended to be invoked
 * from the browser dev console as `Termly.checkConsentLoadOrder()`,
 * which passes the snapshot captured at consent-default-push time.
 *
 * @param {({
 *   hasWaitForUpdate: boolean,
 *   isLate: boolean,
 *   priorGtagEntries: Array,
 * }|null|undefined)} snapshot
 */
export default function logLateConsentDiagnostic(snapshot) {
  if ( !snapshot ) {
    console.info(`${ PREFIX } Late-consent diagnostics are not available yet — `
                 + 'the CMP has not set GCM consent defaults. Try again after the page has initialized.')
    return
  }

  const { hasWaitForUpdate, isLate, priorGtagEntries } = snapshot

  if ( !isLate ) {
    console.info(`${ PREFIX } Load order is correct: Termly set GCM consent defaults before any Google tags fired.`)
    return
  }

  const severity = ( hasWaitForUpdate ) ? 'WARNING' : 'CRITICAL'

  const lines = [
    `${ PREFIX } ${ severity }: Termly set GCM consent defaults AFTER Google tags fired.`,
    '',
  ]

  if ( priorGtagEntries.length > 0 ) {
    lines.push(`  ${ priorGtagEntries.length } gtag('config'|'event') entries fired before Termly:`)

    priorGtagEntries.forEach((entry, index) => {
      lines.push(`    [${ index }] ${ JSON.stringify(Array.from(entry)) }`)
    })
  } else {
    lines.push('  No array-form gtag() entries were visible in the dataLayer, which is typical of '
               + 'GTM-managed tags: GTM fires tags internally without pushing gtag(\'config\'|\'event\') '
               + 'entries. The late-consent verdict comes from Google\'s own load-order signal.')
  }

  lines.push('')
  lines.push('  Remediation:')
  lines.push('    - Move the Termly script above your Google tags in <head>.')
  lines.push('    - If a tag uses Google Tag Gateway (GTG), additional debugging steps are likely necessary. '
             + 'See https://support.termly.io/hc/en-us/articles/46504411586705 for help.')
  lines.push('')

  if ( hasWaitForUpdate ) {
    lines.push('  Note: wait_for_update is already enabled and will queue future tags on this page until Termly reports consent — '
               + 'but the entries above already dispatched before Termly loaded, and wait_for_update can\'t recall them.')
  } else {
    lines.push('  Note: enabling wait_for_update in the Termly dashboard GCM settings helps for tags that fire after Termly loads, '
               + 'but cannot prevent the entries above from dispatching — only moving Termly earlier fixes this pattern.')
  }

  const log = ( hasWaitForUpdate ) ? console.warn : console.error
  log(lines.join('\n'))
}
